import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ETSY_SHOP_URL } from "@/data/products";

/**
 * Hero — massive kinetic type with masked line-by-line reveal.
 * Includes a subtle parallax on the portrait image and floating dust particles.
 */

const LINE_VARIANTS = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: {
      delay: 0.35 + i * 0.12,
      duration: 1.05,
      ease: [0.19, 1, 0.22, 1],
    },
  }),
};

function MaskLine({ children, i }) {
  return (
    <span className="mask-line">
      <motion.span
        custom={i}
        variants={LINE_VARIANTS}
        initial="hidden"
        animate="visible"
      >
        {children}
      </motion.span>
    </span>
  );
}

const DUST = Array.from({ length: 22 }).map((_, i) => ({
  id: i,
  x: (i * 53) % 100,
  y: (i * 37 + 12) % 100,
  d: 4 + ((i * 7) % 9),
  delay: (i % 7) * 0.6,
  size: 1 + (i % 3),
}));

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#1a1412] pt-[68px]"
      data-testid="hero-section"
    >
      {/* Background portrait — parallax */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src="/brand/founder-baobabs.webp"
          alt="EarthTone founder in the collection, baobab avenue at sunset"
          className="w-full h-full object-cover object-[62%_35%] opacity-95"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1412] via-[#1a1412]/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1412] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[#1a1412]/15" />
      </motion.div>

      {/* Floating dust */}
      <div className="absolute inset-0 pointer-events-none">
        {DUST.map((p) => (
          <motion.span
            key={p.id}
            className="absolute rounded-full bg-[#c28b46]"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
              opacity: 0.28,
            }}
            animate={{
              y: [0, -18, 0],
              opacity: [0.15, 0.5, 0.15],
            }}
            transition={{
              duration: p.d,
              delay: p.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Content grid */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 md:pb-32 min-h-[calc(100svh-68px)] flex flex-col justify-end"
      >
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-9 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="eyebrow mb-6 md:mb-10 flex items-center gap-4 flex-wrap"
              data-testid="hero-eyebrow"
            >
              <span className="w-10 h-[1px] bg-[#c28b46]" />
              Genesis 2:7 · <em className="not-italic">EarthTone</em> · <span className="text-[#c62828]">Mind.</span>{" "}
              <span className="text-[#c28b46]">Body.</span>{" "}
              <span className="text-[#2c8a3e]">Earth.</span>
            </motion.p>

            <h1
              className="font-serif text-[#f0e4cc] leading-[0.92] tracking-[-0.02em] text-[52px] sm:text-[72px] md:text-[110px] lg:text-[144px]"
              data-testid="hero-headline"
            >
              <MaskLine i={0}>You Are The</MaskLine>
              <MaskLine i={1}>
                <em className="font-medium italic text-[#c28b46]">Original.</em>
              </MaskLine>
              <MaskLine i={2}>Wear That Truth.</MaskLine>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.9,
                delay: 1.05,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8"
            >
              <a
                href={ETSY_SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-terra"
                data-testid="hero-shop-button"
              >
                Shop The Collection
                <span aria-hidden>→</span>
              </a>
              <a
                href="#guide"
                className="text-[12px] tracking-[0.28em] uppercase text-[#c8b99c] hover:text-[#c28b46] transition-colors link-underline"
                data-testid="hero-guide-link"
              >
                Get the free 29-page guide
              </a>
            </motion.div>
          </div>

          <div className="hidden lg:block col-span-4">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, delay: 1.1 }}
              className="border border-[#362b28] p-6 backdrop-blur-sm bg-[#1a1412]/50"
            >
              <p className="eyebrow mb-3">Chapter 00 · Origin</p>
              <p className="font-serif text-[20px] leading-snug text-[#e8dcc4]">
                “Then the Lord God formed man of the dust of the ground, and breathed into his nostrils the breath of life.”
              </p>
              <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#a89b8c]">
                — Genesis 2:7
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-6 md:left-10 z-10 text-[10px] tracking-[0.34em] uppercase text-[#a89b8c] flex items-center gap-3"
        data-testid="hero-scroll-cue"
      >
        <span className="w-8 h-[1px] bg-[#a89b8c]" />
        Scroll · Remember
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 text-[10px] tracking-[0.34em] uppercase text-[#a89b8c]"
      >
        Est. Dust of the Earth
      </motion.div>
    </section>
  );
}
